/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Radio,
  Space,
  Spin,
  Typography,
  Collapse,
  Tag,
  Empty,
} from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import MainLayout from '../../components/common/MainLayout';
import { useGetPlantingsQuery } from '../../api/plantingsApi';
import { useGetCropTaxonomyTreeQuery } from '../../api/cropsApi';
import PlantingsList from '../../components/plantings/PlantingsList';
import { useRefreshWorld, useSave, useWorld } from '../../contexts/WorldContext';
import type { Crop } from '../../models/Crop';
import { buildScenarioPlanting, buildScenarioWorldIndexes, withPlantingRuntime } from '../../utils/scenarioWorldAdapters';

const styles = {
  clickable: css({ cursor: 'pointer' }),
  cardHeader: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }),
  cropName: css({ fontSize: 16 }),
  latinName: css({ fontSize: 13 }),
  icon: css({ fontSize: 12, color: '#8c8c8c' }),
  tagRow: css({ display: 'flex', gap: 8, alignItems: 'center' }),
  titleMargin: css({ margin: 0 }),
  familyName: css({ fontSize: 16 }),
  grid: css({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 16,
  }),
};

const { Title, Text } = Typography;

interface CropWithCount extends Crop {
  plantingsCount: number;
  familyId?: string | number | null;
  familyName?: string | null;
  familyLatinName?: string | null;
}

const ScenarioPlantingsPage: React.FC = () => {
  const { t } = useTranslation('plantings');
  const { saveId } = useParams<{ saveId: string }>();
  const navigate = useNavigate();
  const world = useWorld();
  const refreshWorld = useRefreshWorld();

  const indexes = useMemo(() => buildScenarioWorldIndexes(world.structure), [world.structure]);
  const plantings = useMemo(
    () => world.structure.plantings.map((planting) => buildScenarioPlanting(
      withPlantingRuntime(planting, world.runtime?.plantings?.[String(planting.id)]),
      indexes,
    )),
    [indexes, world.runtime?.plantings, world.structure.plantings],
  );

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('breadcrumb.home')}</Link> },
            { title: <Link to="/saves">{t('breadcrumb.saves')}</Link> },
            { title: <Link to={`/saves/${saveId}`}>{world.scenario?.title || t('breadcrumb.scenario')}</Link> },
            { title: t('breadcrumb.plantings') },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Alert
          type="info"
          showIcon
          message={t('list.scenarioTitle')}
          description={t('list.scenarioDescription')}
        />

        <Card
          title={t('list.plantingsCount', { count: plantings.length })}
          extra={<Button onClick={() => { void refreshWorld(); }}>{t('common:actions.refresh')}</Button>}
        >
          {plantings.length === 0 ? (
            <Empty description={t('list.scenarioEmpty')} />
          ) : (
            <PlantingsList
              plantings={plantings}
              groupByCrop
              onView={(planting) => navigate(`/saves/${saveId}/plantings/${planting.uuid ?? planting.id}`)}
            />
          )}
        </Card>
      </Space>
    </MainLayout>
  );
};

const FarmBoundPlantingsPage: React.FC = () => {
  const { t } = useTranslation('plantings');
  const { saveId } = useParams<{ saveId: string }>();
  const navigate = useNavigate();
  const save = useSave();
  const farmId = save.farmId;
  const farm = save.farmId ? { id: save.farmId, name: '' } : null;

  // 'active' — что обычно показываем. 'archived' — то, что собрали и убрали с
  // грядки. 'all' — всё, включая dead/archived (полная история).
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');

  const plantingsQueryArg = useMemo(() => {
    if (!saveId) return skipToken;
    return {
      saveId,
      limit: 1000,
      offset: 0,
      ...(statusFilter === 'all' ? {} : { lifecycleStatus: statusFilter }),
    };
  }, [saveId, statusFilter]);

  const {
    data: plantings = [],
    isLoading: plantingsLoading,
    isError: plantingsError,
    refetch: refetchPlantings,
  } = useGetPlantingsQuery(plantingsQueryArg);

  const { data: taxonomy, isLoading: taxonomyLoading } = useGetCropTaxonomyTreeQuery();

  // Группируем посадки по культурам и подсчитываем количество
  const cropsWithCounts = useMemo<CropWithCount[]>(() => {
    const cropMap = new Map<string, CropWithCount>();
    
    plantings.forEach((planting) => {
      if (!planting.crop) return;
      
      const cropKey = String(planting.crop.id);
      if (cropMap.has(cropKey)) {
        cropMap.get(cropKey)!.plantingsCount += 1;
      } else {
        // Найдем семейство для этой культуры через таксономию
        let familyInfo: { id?: string | number; name?: string; latinName?: string } = {};
        
        if (taxonomy && planting.crop.speciesId) {
          for (const family of taxonomy.families) {
            for (const genus of family.genera) {
              const species = genus.species.find(s => String(s.id) === String(planting.crop!.speciesId));
              if (species) {
                familyInfo = {
                  id: family.id,
                  name: family.name,
                  latinName: family.latinName,
                };
                break;
              }
            }
            if (familyInfo.id) break;
          }
        }
        
        cropMap.set(cropKey, {
          ...planting.crop,
          plantingsCount: 1,
          familyId: familyInfo.id,
          familyName: familyInfo.name,
          familyLatinName: familyInfo.latinName,
        });
      }
    });
    
    return Array.from(cropMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [plantings, taxonomy]);

  // Группируем культуры по семействам
  const cropsByFamily = useMemo(() => {
    const familyMap = new Map<string, {
      familyId: string | number;
      familyName: string;
      familyLatinName: string;
      crops: CropWithCount[];
      totalPlantings: number;
    }>();
    
    const unknownCrops: CropWithCount[] = [];
    
    cropsWithCounts.forEach((crop) => {
      if (!crop.familyId) {
        unknownCrops.push(crop);
        return;
      }
      
      const familyKey = String(crop.familyId);
      if (familyMap.has(familyKey)) {
        const family = familyMap.get(familyKey)!;
        family.crops.push(crop);
        family.totalPlantings += crop.plantingsCount;
      } else {
        familyMap.set(familyKey, {
          familyId: crop.familyId,
          familyName: crop.familyName || t('list.unnamedFamily'),
          familyLatinName: crop.familyLatinName || '',
          crops: [crop],
          totalPlantings: crop.plantingsCount,
        });
      }
    });
    
    const families = Array.from(familyMap.values()).sort((a, b) => 
      a.familyName.localeCompare(b.familyName)
    );
    
    return { families, unknownCrops };
  }, [cropsWithCounts, t]);

  const farmName = farm?.name ?? t('location.farmNamed', { name: farmId ?? '' });
  const skipQueries = !farmId;
  
  const handleNavigateToCrop = (cropId: string | number) => {
    navigate(`/saves/${saveId}/crops/${cropId}/plantings`);
  };

  const renderCropCard = (crop: CropWithCount) => (
    <Card
      key={crop.id}
      hoverable
      onClick={() => handleNavigateToCrop(crop.id)}
      css={styles.clickable}
    >
      <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
        <div css={styles.cardHeader}>
          <div>
            <Text strong css={styles.cropName}>
              {crop.name}
            </Text>
            {crop.latinName && (
              <div>
                <Text type="secondary" italic css={styles.latinName}>
                  {crop.latinName}
                </Text>
              </div>
            )}
          </div>
          <RightOutlined css={styles.icon} />
        </div>
        <div css={styles.tagRow}>
          <Tag color="blue">{t('list.plantingsTag', { count: crop.plantingsCount })}</Tag>
          {crop.speciesName && (
            <Text type="secondary" css={commonStyles.smallText}>
              {t('list.species', { name: crop.speciesName })}
            </Text>
          )}
        </div>
      </Space>
    </Card>
  );

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('breadcrumb.home')}</Link> },
            { title: <Link to="/saves">{t('breadcrumb.saves')}</Link> },
            { title: <Link to={`/saves/${saveId}`}>{farmName}</Link> },
            { title: t('breadcrumb.plantings') },
          ]}
        />
      }
    >
      {skipQueries ? (
        <Card>{t('list.farmNotFound')}</Card>
      ) : (
        <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
          <Card>
            <Space direction="vertical" size={4}>
              <Title level={4} css={styles.titleMargin}>
                {t('list.farmTitle')}
              </Title>
              <Text type="secondary">
                {t('list.farmDescription')}
              </Text>
            </Space>
          </Card>

          <Card
            title={t('list.cropsWithPlantings', { count: cropsWithCounts.length })}
            extra={
              <Space wrap>
                <Radio.Group
                  size="small"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  options={[
                    { label: t('list.filter.active'), value: 'active' },
                    { label: t('list.filter.archived'), value: 'archived' },
                    { label: t('list.filter.all'), value: 'all' },
                  ]}
                />
                <Button onClick={() => refetchPlantings()}>{t('common:actions.refresh')}</Button>
              </Space>
            }
          >
            {plantingsLoading || taxonomyLoading ? (
              <Spin />
            ) : plantingsError ? (
              <Card type="inner">{t('list.loadFailed')}</Card>
            ) : cropsWithCounts.length === 0 ? (
              <Empty description={t('list.farmEmpty')} />
            ) : (
              <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
                {/* Культуры с известным семейством */}
                {cropsByFamily.families.length > 0 && (
                  <Collapse
                    items={cropsByFamily.families.map((family) => ({
                      key: String(family.familyId),
                      label: (
                        <Space>
                          <Text strong css={styles.familyName}>
                            {family.familyName}
                          </Text>
                          {family.familyLatinName && (
                            <Text type="secondary" italic>
                              {family.familyLatinName}
                            </Text>
                          )}
                          <Tag color="green">{t('list.plantingsTag', { count: family.totalPlantings })}</Tag>
                        </Space>
                      ),
                      children: (
                        <div
                          css={styles.grid}
                        >
                          {family.crops.map(renderCropCard)}
                        </div>
                      ),
                    }))}
                    defaultActiveKey={cropsByFamily.families.map((f) => String(f.familyId))}
                  />
                )}

                {/* Культуры без семейства */}
                {cropsByFamily.unknownCrops.length > 0 && (
                  <Card title={t('list.noFamily')} type="inner">
                    <div
                      css={styles.grid}
                    >
                      {cropsByFamily.unknownCrops.map(renderCropCard)}
                    </div>
                  </Card>
                )}
              </Space>
            )}
          </Card>
        </Space>
      )}
    </MainLayout>
  );
};

const PlantingsPage: React.FC = () => {
  const save = useSave();
  if (save.type === 'scenario') {
    return <ScenarioPlantingsPage />;
  }
  return <FarmBoundPlantingsPage />;
};

export default PlantingsPage;
